/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
 */

/**
 * Checks if option value is empty
 * @param {OptionObject|string|number} opt
 * @returns {boolean}
 */
export function isEmpty(opt) {
    if (opt === 0) {
        return false;
    }
    if (Array.isArray(opt) && opt.length === 0) {
        return true;
    }
    return !opt;
}

/**
 * Check if input string includes search query
 * @param {string} str - input string
 * @param {string} query - search query
 * @returns {boolean}
 */
export function includesQuery(str, query) {
    let normalizedStr = str;
    if (str === void 0) {
        normalizedStr = 'undefined';
    }
    if (str === null) {
        normalizedStr = 'null';
    }
    if (str === false) {
        normalizedStr = 'false';
    }
    const text = normalizedStr.toString().toLowerCase();
    return text.indexOf(query.trim()) !== -1;
}

/**
 * Filter options array by search query
 * @param {Array.<OptionObject|string|number>} options - array of options to filter
 * @param {string} search - search query
 * @param {(option: OptionObject|string|number) => string} getOptionLabel - function to get option label
 * @returns {Array.<OptionObject|string|number>} - filtered array of options
 */
export function filterOptions(options, search, getOptionLabel) {
    return options.filter(option => includesQuery(getOptionLabel(option), search));
}

/**
 * Create a handler for filtering options with groups by search query
 * @param {string} search - search query
 * @param {string} valuesKey - key for group values
 * @param {string} groupLabel - key for group label
 * @param {(option: OptionObject|string|number) => string} getOptionLabel - function to get option label
 * @returns {(groups: Array.<OptionObject>) => Array.<OptionObject>}
 */
export function filterGroups(search, valuesKey, groupLabel, getOptionLabel) {
    return groups =>
        groups.map(group => {
            if (!group[valuesKey]) {
                return [];
            }
            const groupOptions = filterOptions(group[valuesKey], search, getOptionLabel);

            return groupOptions.length
                ? {
                    [groupLabel]: group[groupLabel],
                    [valuesKey]: groupOptions
                }
                : [];
        });
}

/**
 * Flatten options array by adding option entries for groups
 * @param {string} valuesKey - Key for values to retreive normal options
 * @param {string} label - Key to use for group label
 * @returns {(options: Array.<OptionObject>) => Array.<OptionObject>}
 */
export function flattenOptions(valuesKey, label) {
    return options =>
        options.reduce((accumulator, value) => {
            if (value[valuesKey] && value[valuesKey].length) {
                accumulator.push({
                    $groupLabel: value[label],
                    $isLabel: true
                });
                return [...accumulator, ...value[valuesKey]];
            }
            return accumulator;
        }, []);
}

/**
 * Run functions in a pipeline with some starting argument
 * @param  {...Function} functions
 * @returns {(res: any) => any}
 */
export function pipeline(...functions) {
    return res => functions.reduce((prevResult, fn) => fn(prevResult), res);
}
